/**
 * Portal Roles and Permissions
 *
 * Defines role-based access control for internal team members accessing the admin portal.
 * These are distinct from business-facing user roles (UserRole).
 */

/**
 * Portal role types for internal team members
 * These are users who access the admin portal (not business-facing users)
 */
export enum PortalRole {
  // Platform-level roles
  SUPER_ADMIN = 'super_admin',
  PLATFORM_ADMIN = 'platform_admin',

  // Department-specific roles
  BUSINESS_SUCCESS = 'business_success',
  SUPPORT = 'support',
  SOLUTIONS = 'solutions',
  DEVELOPER = 'developer',
  FINANCE = 'finance',
  ANALYST = 'analyst',

  // Basic access
  VIEWER = 'viewer',
}

/**
 * Portal permissions
 * Granular permissions for different portal features
 */
export enum PortalPermission {
  // Business management
  BUSINESSES_READ = 'businesses:read',
  BUSINESSES_WRITE = 'businesses:write',
  BUSINESSES_DELETE = 'businesses:delete',

  // User management
  USERS_READ = 'users:read',
  USERS_WRITE = 'users:write',
  USERS_DELETE = 'users:delete',

  // Billing
  BILLING_READ = 'billing:read',
  BILLING_WRITE = 'billing:write',

  // Analytics
  ANALYTICS_READ = 'analytics:read',
  ANALYTICS_EXPORT = 'analytics:export',

  // AI Models
  MODELS_READ = 'models:read',
  MODELS_WRITE = 'models:write',

  // API Keys
  API_KEYS_READ = 'api_keys:read',
  API_KEYS_WRITE = 'api_keys:write',

  // Audit logs
  AUDIT_READ = 'audit:read',
  AUDIT_EXPORT = 'audit:export',

  // Team management
  TEAM_READ = 'team:read',
  TEAM_WRITE = 'team:write',

  // Settings
  SETTINGS_READ = 'settings:read',
  SETTINGS_WRITE = 'settings:write',

  // All permissions (super admin)
  ALL = '*',
}

/**
 * Role to permissions mapping
 *
 * Defines which permissions each role has by default.
 * SUPER_ADMIN has all permissions via the wildcard.
 */
export const ROLE_PERMISSIONS: Record<PortalRole, PortalPermission[]> = {
  [PortalRole.SUPER_ADMIN]: [PortalPermission.ALL],

  [PortalRole.PLATFORM_ADMIN]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.BUSINESSES_WRITE,
    PortalPermission.BUSINESSES_DELETE,
    PortalPermission.USERS_READ,
    PortalPermission.USERS_WRITE,
    PortalPermission.USERS_DELETE,
    PortalPermission.BILLING_READ,
    PortalPermission.BILLING_WRITE,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.ANALYTICS_EXPORT,
    PortalPermission.MODELS_READ,
    PortalPermission.MODELS_WRITE,
    PortalPermission.API_KEYS_READ,
    PortalPermission.API_KEYS_WRITE,
    PortalPermission.AUDIT_READ,
    PortalPermission.AUDIT_EXPORT,
    PortalPermission.TEAM_READ,
    PortalPermission.TEAM_WRITE,
    PortalPermission.SETTINGS_READ,
    PortalPermission.SETTINGS_WRITE,
  ],

  [PortalRole.BUSINESS_SUCCESS]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.BUSINESSES_WRITE,
    PortalPermission.USERS_READ,
    PortalPermission.BILLING_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.ANALYTICS_EXPORT,
    PortalPermission.AUDIT_READ,
  ],

  [PortalRole.SUPPORT]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.USERS_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.AUDIT_READ,
    PortalPermission.API_KEYS_READ,
  ],

  [PortalRole.SOLUTIONS]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.BUSINESSES_WRITE,
    PortalPermission.USERS_READ,
    PortalPermission.USERS_WRITE,
    PortalPermission.BILLING_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.MODELS_READ,
  ],

  [PortalRole.DEVELOPER]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.ANALYTICS_EXPORT,
    PortalPermission.MODELS_READ,
    PortalPermission.MODELS_WRITE,
    PortalPermission.AUDIT_READ,
    PortalPermission.AUDIT_EXPORT,
    PortalPermission.API_KEYS_READ,
  ],

  [PortalRole.FINANCE]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.BILLING_READ,
    PortalPermission.BILLING_WRITE,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.ANALYTICS_EXPORT,
  ],

  [PortalRole.ANALYST]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.USERS_READ,
    PortalPermission.BILLING_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.ANALYTICS_EXPORT,
    PortalPermission.AUDIT_READ,
    PortalPermission.AUDIT_EXPORT,
  ],

  [PortalRole.VIEWER]: [
    PortalPermission.BUSINESSES_READ,
    PortalPermission.USERS_READ,
    PortalPermission.BILLING_READ,
    PortalPermission.ANALYTICS_READ,
    PortalPermission.MODELS_READ,
    PortalPermission.SETTINGS_READ,
  ],
};

/**
 * Get permissions for a role
 *
 * @param role - Portal role to get permissions for
 * @returns Array of permissions for the role
 */
export function getPermissionsForRole(role: PortalRole): PortalPermission[] {
  return ROLE_PERMISSIONS[role] || [];
}

/**
 * Check if role has permission
 *
 * @param role - Portal role to check
 * @param permission - Permission to check for
 * @returns True if role has the permission (either directly or via ALL wildcard)
 */
export function hasPermission(role: PortalRole, permission: PortalPermission): boolean {
  const permissions = getPermissionsForRole(role);
  return permissions.includes(PortalPermission.ALL) || permissions.includes(permission);
}
